import React, { useEffect, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import TestCloseButton from "../components/TestCloseButton";
import { api, formatApiErrorDetail } from "../lib/api";
import ReactMarkdown from "react-markdown";
import {
  Briefcase, PaperPlaneRight, Sparkle, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Beroepentest — AI chatbot that proposes one best-matching profession from
 * all test data + CV, lets the coachee explore alternatives, and ends with
 * a structured risk-analysis report. Mirrors the conflict test page
 * (optimistic counter bump, defensive refetch on long-AI timeout).
 */
export default function CoacheeProfessionPage() {
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (own) {
          const { data: s } = await api.get(`/coachees/${own.id}/profession-test`);
          setSession(s);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    // Bij een nieuw bericht (lang AI-rapport of korte gebruikersvraag):
    // scroll zodat het BEGIN van het laatste bericht bovenaan in het
    // scrollvak staat — de gebruiker leest van boven naar beneden i.p.v.
    // meteen naar de onderkant te springen.
    const last = scrollRef.current.lastElementChild;
    if (last) {
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      const elTop = last.getBoundingClientRect().top;
      scrollRef.current.scrollTop += elTop - containerTop;
    }
  }, [session?.messages?.length]);

  useEffect(() => {
    if (!sending && !starting && inputRef.current && !inputRef.current.disabled) {
      inputRef.current.focus();
    }
  }, [sending, starting, session?.messages?.length]);

  const handleStart = async () => {
    if (!coachee) return;
    setError("");
    setStarting(true);
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/profession-test/start`,
        null,
        { timeout: 180000 },
      );
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!coachee || !draft.trim() || sending) return;
    const content = draft.trim();
    setError("");
    setSending(true);
    setSession((prev) => {
      const base = prev || { messages: [], user_message_count: 0, max_user_messages: 40 };
      return {
        ...base,
        messages: [
          ...(base.messages || []),
          { role: "user", content, ts: new Date().toISOString() },
        ],
        user_message_count: (base.user_message_count || 0) + 1,
      };
    });
    setDraft("");
    const prevUserCount = (session?.user_message_count || 0);
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/profession-test/message`,
        { content },
        { timeout: 180000 },
      );
      setSession(data);
    } catch (err) {
      // Defensive refetch — long AI calls (eindrapport) kunnen via de proxy
      // sneuvelen terwijl de backend wel doorgaat.
      let recovered = false;
      try {
        const { data: fresh } = await api.get(`/coachees/${coachee.id}/profession-test`);
        const freshCount = fresh?.user_message_count || 0;
        const lastFresh = (fresh?.messages || []).slice(-1)[0];
        if (freshCount > prevUserCount && lastFresh?.role === "assistant") {
          setSession(fresh);
          recovered = true;
        }
      } catch (refetchErr) {
        // Defensive refetch zelf is mislukt — niet fataal, we vallen
        // terug op de rollback hieronder. Loggen voor debugging.
        console.warn("Beroepentest defensive refetch failed:", refetchErr?.message || refetchErr);
      }
      if (!recovered) {
        setSession((prev) => {
          if (!prev) return prev;
          const msgs = [...(prev.messages || [])];
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].role === "user" && msgs[i].content === content) {
              msgs.splice(i, 1); break;
            }
          }
          return { ...prev, messages: msgs, user_message_count: Math.max(0, (prev.user_message_count || 0) - 1) };
        });
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setDraft(content);
      }
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  const used = session?.user_message_count ?? 0;
  const cap = session?.max_user_messages ?? 40;
  const limitReached = session?.limit_reached === true || used >= cap;
  const completed = session?.completed === true || !!session?.completed_at;
  const inputLocked = limitReached || completed;
  const hasMessages = (session?.messages?.length || 0) > 0;

  return (
    <AppLayout title="Beroepentest" breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Beroepentest" }]}>
      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : !coachee ? (
        <div className="text-zinc-500">Coachee-profiel niet gevonden.</div>
      ) : (
        <div className="max-w-3xl space-y-4" data-testid="profession-page">
          <div className="neuro-card-bold p-5 bg-white">
            <div className="flex items-start gap-3">
              <Briefcase size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold tracking-tight">Beroepentest</h2>
                <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                  De AI loopbaan-job-coach gebruikt al je testresultaten en je CV om met jou samen
                  één concreet beroep vast te stellen. Bij elke suggestie krijg je een
                  match-waarschijnlijkheid (De Finetti). Aan het einde maken we een risico-analyse
                  en een rapport met de benodigde opleidingen.
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                  <span data-testid="profession-counter">
                    Berichten gebruikt: <strong className="text-zinc-900">{used}</strong> / {cap}
                  </span>
                  {completed && (
                    <span className="text-[#43A047] font-medium" data-testid="profession-completed-badge">
                      ✓ Test voltooid — je eindrapport staat onderaan dit gesprek.
                    </span>
                  )}
                  {limitReached && !completed && (
                    <span className="text-[#E53935] font-medium" data-testid="profession-limit-reached">
                      Limiet bereikt — vraag je work coach om te resetten.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="profession-error">
              <WarningCircle size={18} className="shrink-0 mt-0.5" /> <div>{error}</div>
            </div>
          )}

          <div
            ref={scrollRef}
            className="neuro-card p-4 bg-white h-[480px] overflow-y-auto space-y-3"
            data-testid="profession-thread"
          >
            {!hasMessages && !starting && (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <Sparkle size={32} weight="duotone" className="text-[#1E88E5] mb-3" />
                <p className="text-sm text-zinc-600 max-w-sm leading-relaxed">
                  Klaar om je ideale beroep te ontdekken? De AI begint met één voorstel
                  gebaseerd op al je data, en je kunt vandaaruit verkennen.
                </p>
                <button
                  type="button"
                  onClick={handleStart}
                  data-testid="profession-start-btn"
                  className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
                >
                  Start de test
                </button>
              </div>
            )}
            {hasMessages && session.messages.map((m, i) => (
              <Bubble key={m.ts ? `${m.ts}-${m.role}` : `i-${i}`} role={m.role} content={m.content} />
            ))}
            {(sending || starting) && <Bubble role="assistant" content="..." typing />}
          </div>

          <form onSubmit={handleSend} className="flex gap-2" data-testid="profession-form">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                completed
                  ? "Test voltooid — je eindrapport staat hierboven in de chat."
                  : limitReached
                    ? "Limiet bereikt. Vraag je work coach om te resetten."
                    : hasMessages
                      ? "Typ je antwoord..."
                      : "Start eerst de test met de knop hierboven."
              }
              disabled={!hasMessages || inputLocked || sending}
              data-testid="profession-input"
              rows={2}
              autoFocus
              className="flex-1 px-3 py-2.5 border-2 border-zinc-400 bg-white rounded-sm text-sm shadow-sm caret-zinc-900 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/15 focus:shadow-md disabled:bg-zinc-50 disabled:text-zinc-400 disabled:border-zinc-200 disabled:shadow-none resize-none transition-all"
            />
            <button
              type="submit"
              disabled={!hasMessages || inputLocked || sending || !draft.trim()}
              data-testid="profession-send-btn"
              className="self-end inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <PaperPlaneRight size={16} /> {sending ? "..." : "Verstuur"}
            </button>
          </form>
        </div>
      )}
      {completed && <TestCloseButton testId="profession-close-btn" />}
    </AppLayout>
  );
}

function Bubble({ role, content, typing = false }) {
  const isUser = role === "user";
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")} data-testid={isUser ? "profession-msg-user" : "profession-msg-ai"}>
      <div
        className={
          "max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {typing ? (
          <span className="inline-flex gap-1 items-end text-zinc-500">
            <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
          </span>
        ) : isUser ? (
          content
        ) : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: `${delay}ms` }} />;
}
